import { useAppDispatch } from 'app/hooks';
import MenuForm from 'components/forms/MenuForm';
import MenuItemForm from 'components/forms/MenuItemForm';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import { ModalDialogLink } from 'components/modal/Modal';
import {
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { IMenu } from 'features/menu/menuAPI';
import { IMenuItem } from 'features/menu/menuItemAPI';
import { deleteMenuItemAsync } from 'features/menu/menuItemSlice';
import { deleteMenuAsync } from 'features/menu/menuSlice';
import { AnyOrderObject } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useUser } from 'selectors/userSelectors';

type MenuViewProps = {
  menu: IMenu;
};

export default function MenuView(props: MenuViewProps) {
  const { menu } = props;
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmDelete();
  const { userIsManager } = useUser();

  function handleRemoveMenu(menu: IMenu) {
    function doDelete(): boolean {
      dispatch(
        deleteMenuAsync({
          id: menu.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: (
          <FormattedMessage
            id="MENUS.DELETE_CONFIRM"
            values={{
              menu: menu.title,
            }}
          />
        ),
        prompt2: <FormattedMessage id="MENUS.DELETE_CONFIRM_2" />,
      },
      // again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }

  function handleRemoveMenuItem(item: IMenuItem) {
    function doDelete(): boolean {
      dispatch(
        deleteMenuItemAsync({
          id: item.id!,
          menuId: menu.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: (
          <FormattedMessage
            id="MENUS.ITEMS.DELETE_CONFIRM"
            values={{
              item: item.title,
            }}
          />
        ),
        prompt2: null,
      },
      // again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }

  // const reorderMenuItems = useCallback(
  //   async (
  //     sortableInfo: SortableInfo,
  //     sourceItem: AnyOrderObject,
  //     sourceList: SortableListInfo,
  //     destinationItem?: AnyOrderObject,
  //     destinationList?: SortableListInfo,
  //     copy?: boolean,
  //   ) => {
  //     const data: IMenuItem = {
  //       ...sourceItem,
  //     };
  //     // set the new order attribute
  //     const before = destinationItem
  //       ? ['top', 'left'].includes(
  //           extractClosestEdge(
  //             sortableInfo.location.current.dropTargets[0].data,
  //           ) ?? '',
  //         )
  //       : false;
  //     data.farey_order = getFareyOrderUsingId(
  //       sortByOrder(
  //         destinationList?.items?.filter(
  //           (item) => !item.pinned,
  //         ) as AnyOrderObject[],
  //       ),
  //       sameId(sourceList, destinationList) ? sourceItem.id : -1, // -1 if moving between lists
  //       destinationItem ? destinationItem.id : -1,
  //       before,
  //     );
  //     dispatch(patchMenuItemAsync({ data: data })).then(
  //       (response: { payload: any }) => {
  //         switch (response.payload?.response.status) {
  //           case 200:
  //           case 201:
  //             // console.log('Blocks updated');
  //             break;
  //           case 500:
  //           default:
  //             // TODO: need a better action here, possibly force reload.
  //             console.log(
  //               'Menu not updated due to internal error. Please reload the page.',
  //             );
  //             break;
  //         }
  //       },
  //     );
  //   },
  //   [dispatch],
  // );

  if (!userIsManager) return null;

  const listInfo: SortableListInfo = {
    id: menu.id as number,
    listType: SortableListType.SortableList,
    items: sortByOrder((menu.items ?? []) as AnyOrderObject[]),
  };
  return (
    <div className="menu_view">
      <div className="menu_header">
        <h1>{menu.title}</h1>
        &nbsp;—&nbsp;
        {menu.locale}
        <ModalDialogLink
          className="icon_button"
          component={MenuForm}
          componentProps={{ menu: menu }}
        >
          <Icon inline symbol={IconSymbol.pencil} size={14} />
        </ModalDialogLink>
        <Icon
          inline
          className="icon_button"
          symbol={IconSymbol.bin}
          size={14}
          onClick={() => handleRemoveMenu(menu)}
        />
      </div>
      <SortableList
        className="menu_items"
        listInfo={listInfo}
        accepting={[SortableItemType.SortableItem]}
      >
        {listInfo.items?.map((item) => (
          <SortableItem
            key={item.id}
            listInfo={listInfo}
            item={item as AnyOrderObject}
            itemType={SortableItemType.SortableItem}
            addHandle={false}
          >
            <Link to={`/${item.slug}`}>{item.title}</Link>
            {item.text ? <>&nbsp;— {item.text}</> : null}
            <ModalDialogLink
              className="icon_button"
              component={MenuItemForm}
              componentProps={{ menu: menu, item: item }}
            >
              <Icon inline symbol={IconSymbol.pencil} size={14} />
            </ModalDialogLink>
            <Icon
              inline
              className="icon_button"
              symbol={IconSymbol.bin}
              size={14}
              onClick={() => handleRemoveMenuItem(item)}
            />
          </SortableItem>
        ))}
      </SortableList>
      <ModalDialogLink
        className="nav_item"
        component={MenuItemForm}
        componentProps={{ menu: menu }}
      >
        <FormattedMessage id={'MENUS.ITEMS.NEW'} />
      </ModalDialogLink>
    </div>
  );
}
